if (!customElements.get('custom-cart-drawer')) {
  class CustomCartDrawer extends HTMLElement {
    constructor() {
      super();

      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.isRefreshing = false;
    }

    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('keydown', this.handleDocumentKeydown);
      this.addEventListener('change', this.handleChange);
      this.addEventListener('focusout', this.handleFocusOut);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('keydown', this.handleDocumentKeydown);
      this.removeEventListener('change', this.handleChange);
      this.removeEventListener('focusout', this.handleFocusOut);
    }

    async handleDocumentClick(event) {
      const cartTrigger = event.target.closest('custom-header .nav-item--cart a');
      if (cartTrigger) {
        event.preventDefault();
        await this.open(cartTrigger);
        return;
      }

      if (event.target.closest('[data-cart-drawer-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      const quantityButton = event.target.closest('[data-cart-drawer-quantity]');
      if (quantityButton) {
        event.preventDefault();
        await this.handleQuantityButton(quantityButton);
        return;
      }

      const removeButton = event.target.closest('[data-cart-drawer-remove]');
      if (removeButton) {
        event.preventDefault();
        await this.updateLine(removeButton.dataset.line, 0);
      }
    }

    handleDocumentKeydown(event) {
      if (event.key === 'Escape' && this.dataset.open === 'true') {
        this.close();
      }
    }

    async handleChange(event) {
      const quantityInput = event.target.closest('[data-cart-drawer-input]');
      if (!quantityInput) return;

      await this.updateLine(quantityInput.dataset.line, quantityInput.value);
    }

    async handleFocusOut(event) {
      const quantityInput = event.target.closest('[data-cart-drawer-input]');
      if (!quantityInput) return;

      if (event.relatedTarget && quantityInput.closest('.cart-drawer__quantity')?.contains(event.relatedTarget)) {
        return;
      }

      await this.updateLine(quantityInput.dataset.line, quantityInput.value);
    }

    async handleQuantityButton(button) {
      const quantityWrapper = button.closest('.cart-drawer__quantity');
      const quantityInput = quantityWrapper?.querySelector('[data-cart-drawer-input]');
      if (!quantityInput) return;

      const currentValue = parseInt(quantityInput.value || '1', 10);
      const nextValue = button.dataset.cartDrawerQuantity === 'increase'
        ? currentValue + 1
        : Math.max(currentValue - 1, 0);

      quantityInput.value = nextValue;
      await this.updateLine(quantityInput.dataset.line, nextValue);
    }

    async open(trigger) {
      if (trigger instanceof HTMLElement) {
        this.lastTrigger = trigger;
      }

      this.dataset.open = 'true';
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cart-drawer-open');

      await this.refresh();

      const closeButton = this.querySelector('.cart-drawer__close');
      if (closeButton) {
        closeButton.focus();
      }
    }

    close() {
      this.dataset.open = 'false';
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-drawer-open');

      if (this.lastTrigger && document.contains(this.lastTrigger)) {
        this.lastTrigger.focus();
      }
    }

    async refresh() {
      if (this.isRefreshing) return;

      this.isRefreshing = true;
      this.classList.add('cart-drawer__loading');

      try {
        const sectionWrapperId = this.closest('.shopify-section')?.id || '';
        const sectionId = sectionWrapperId.replace(/^shopify-section-/, '');
        const sectionQuery = sectionId ? encodeURIComponent(sectionId) : 'cart-drawer';
        const response = await fetch(`/?section_id=${sectionQuery}`);
        if (!response.ok) throw new Error(`Cart drawer refresh failed: ${response.status}`);

        const html = await response.text();
        const replacement = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('custom-cart-drawer');

        if (replacement) {
          this.innerHTML = replacement.innerHTML;
        }

        await this.refreshHeaderCount();
      } catch (error) {
        console.error(error);
      } finally {
        this.classList.remove('cart-drawer__loading');
        this.isRefreshing = false;
      }
    }

    async refreshHeaderCount() {
      const cartCount = document.querySelector('custom-header .nav-item .cart-count');
      if (!cartCount) return;

      const response = await fetch('/?section_id=header-cart-icon');
      if (!response.ok) return;

      const html = await response.text();
      const replacement = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('#shopify-section-header-cart-icon');

      if (!replacement) return;

      cartCount.innerHTML = replacement.innerHTML.trim();
      cartCount.setAttribute('data-hidden', 'false');
    }

    async updateLine(line, quantity) {
      const parsedQuantity = Math.max(parseInt(quantity || '0', 10), 0);
      const payload = new FormData();

      payload.append('line', `${line}`);
      payload.append('quantity', `${parsedQuantity}`);

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = payload;

      this.classList.add('cart-drawer__loading');

      try {
        const response = await fetch(window.routes.cart_change_url, config);
        if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);

        await response.json();
        await this.refresh();
      } catch (error) {
        console.error(error);
      } finally {
        this.classList.remove('cart-drawer__loading');
      }
    }
  }

  customElements.define('custom-cart-drawer', CustomCartDrawer);
}
